import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react'
import { ApiError } from '../../api/client'
import {
  GZ_KEY,
  gzCompanies,
  gzDeleteConnection,
  gzEnablePush,
  gzLink,
  gzNetworks,
  gzPageQuery,
  gzPushOff,
  gzPushStatus,
  gzSaveConnection,
  gzSyncNow,
  gzTestConnection,
  gzUnlink,
  type GzCompany,
  type GzConnection,
  type GzConnectionForm,
  type GzLink,
  type GzNetwork,
  type GzPage,
  type GzPush,
} from '../../api/settingsGravityZone'
import { flowAge } from '../../securitee/XdrFlowAlert'
import { toast } from '../../ui/notify'
import { Spinner } from '../../ui/Spinner'
import './gravity-zone.css'

/**
 * Settings > Securitee > GravityZone: conexiunile la consola GravityZone Cloud
 * (a furnizorului, impartita, sau consola proprie a firmei) si legatura firmei
 * din antet cu firma ei din GZ. Regulile sunt in `GravityZoneSettings` (API);
 * statiile le citeste cronul ph4, numai pentru firmele legate.
 */

type FieldError = { field: string; message: string }

const FORM_FIELDS = ['name', 'access_url', 'api_key']

/* Adresa GravityZone Cloud cea mai des intalnita; completata, nu doar exemplu gri (parea scrisa si pleca goala). */
const CLOUD_URL = 'https://cloudgz.gravityzone.bitdefender.com/api'

function errorOf(err: unknown): FieldError {
  const details = err instanceof ApiError ? err.details : undefined
  return { field: typeof details?.field === 'string' ? details.field : '', message: err instanceof Error ? err.message : 'Something went wrong.' }
}

/* Pasii din `GravityZonePush` (API), in ordine. */
const PUSH_STEPS = ['Save the push settings in GravityZone', 'GravityZone accepted them', 'Send a test event', 'Test event reached OptimumDesk', 'Push active']

/* Cat de des intreaba ecranul starea cat asteapta proba (pasul 4). */
const PUSH_POLL_MS = 2000

/* Pasul 4 picat: GZ reincearca singur, uneori abia dupa ~30 de minute; se intreaba rar, pana porneste. */
const LATE_POLL_MS = 15000

/* Intreaba la `ms` starea push-ului conexiunilor din `key` (ID-uri cu virgula). */
function usePushPoll(key: string, ms: number, onAnswer: (id: number, push: GzPush) => void) {
  useEffect(() => {
    if (key === '') return
    const ids = key.split(',').map(Number)
    const timer = window.setInterval(() => {
      for (const id of ids) {
        gzPushStatus(id)
          .then((answer) => onAnswer(id, answer.push))
          .catch(() => undefined)
      }
    }, ms)
    return () => window.clearInterval(timer)
    // onAnswer scrie in clientul de query, care nu se schimba
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, ms])
}

function pushLabel(push: GzPush): ReactNode {
  if (push.status === 'active' && push.health === 'silent') {
    return (
      <>
        <span className="label label-danger">Stopped</span>
        <div className="gz-push-seen">no events for {flowAge(push.silent_minutes ?? 0)}</div>
      </>
    )
  }
  if (push.status === 'active') {
    return (
      <>
        <span className="label label-success">On</span>
        {push.last_event_at && <div className="gz-push-seen">last event {push.last_event_at}</div>}
      </>
    )
  }
  if (push.status === 'enabling') return <span className="label label-info">Turning on · step {Math.min(push.step + 1, push.steps)} of {push.steps}</span>
  if (push.status === 'failed') return <span className="label label-danger">Failed at step {push.failed_step}</span>
  return <span className="label label-default">Off</span>
}

function stepState(push: GzPush, n: number): 'done' | 'running' | 'failed' | 'waiting' {
  if (n <= push.step) return 'done'
  if (push.status === 'enabling' && n === push.step + 1) return 'running'
  if (push.status === 'failed' && n === push.failed_step) return 'failed'
  return 'waiting'
}

const STEP_ICON = {
  done: 'fas fa-check-circle text-success',
  running: 'fas fa-circle-notch fa-spin text-info',
  failed: 'fas fa-times-circle text-danger',
  waiting: 'far fa-circle gz-muted',
} as const

function PushSteps({ connection, push }: { connection: GzConnection; push: GzPush }) {
  return (
    <div className="gz-push-box">
      <h5>
        <strong>Push for {connection.name}</strong>
      </h5>
      <ol className="gz-push-steps" aria-label={`Push steps for ${connection.name}`}>
        {PUSH_STEPS.map((label, i) => {
          const state = stepState(push, i + 1)
          return (
            <li key={label} data-state={state}>
              <i className={STEP_ICON[state]} aria-hidden="true"></i>
              <span>
                {i + 1}. {label}
              </span>
              {state === 'failed' && push.error && <div className="gz-push-error">{push.error}</div>}
            </li>
          )
        })}
      </ol>
    </div>
  )
}

function connectionStatus(c: GzConnection): ReactNode {
  if (c.check_ok === true) return <span className="label label-success">Connected · {c.companies_seen ?? 0} companies</span>
  if (c.check_ok === false) return <span className="label label-danger">Failed: {c.check_error ?? 'no answer'}</span>
  return <span className="label label-default">Not tested</span>
}

/** Enable / Retry Push; dupa un 429 de la GZ numara secundele pana cand GZ primeste iar apeluri. */
function PushButton({ name, retry, wait, busy, onClick }: { name: string; retry: boolean; wait: number; busy: boolean; onClick: () => void }) {
  const [left, setLeft] = useState(wait)
  useEffect(() => {
    if (left <= 0) return
    const timer = window.setTimeout(() => setLeft((n) => n - 1), 1000)
    return () => window.clearTimeout(timer)
  }, [left])

  return (
    <button type="button" className="btn btn-primary btn-xs" aria-label={`${retry ? 'Retry' : 'Enable'} Push for ${name}`} disabled={busy || left > 0} onClick={onClick}>
      {left > 0 ? `Retry in ${left} s` : retry ? 'Retry Push' : 'Enable Push'}
    </button>
  )
}

export function GravityZonePanel() {
  const client = useQueryClient()
  const [pollUntil, setPollUntil] = useState(0)
  /* Cat asteapta prima citire sau una pornita din Sync now, pagina se reciteste singura. */
  const page = useQuery({
    ...gzPageQuery(),
    refetchInterval: (query) => ((query.state.data?.link && query.state.data.link.synced_at === null) || Date.now() < pollUntil ? 10000 : false),
  })
  const [editing, setEditing] = useState<GzConnectionForm | null>(null)
  const [tests, setTests] = useState<Record<number, string>>({})
  const [busy, setBusy] = useState(false)
  /* Conexiunile pornite din ecranul asta: lista de pasi ramane si dupa ce push-ul e activ. */
  const [watched, setWatched] = useState<number[]>([])

  const merge = (id: number, push: GzPush) =>
    client.setQueryData<GzPage>(GZ_KEY, (old) => (old ? { ...old, connections: old.connections.map((c) => (c.id === id ? { ...c, push } : c)) } : old))
  const connections = page.data?.connections ?? []
  const enablingKey = connections.filter((c) => c.push?.status === 'enabling').map((c) => c.id).join(',')
  const lateKey = connections.filter((c) => c.push?.status === 'failed' && c.push.failed_step === 4).map((c) => c.id).join(',')

  /* Pasii 4 si 5 se fac la citirea starii: cat asteapta proba, ecranul intreaba. */
  usePushPoll(enablingKey, PUSH_POLL_MS, (id, push) => {
    merge(id, push)
    setWatched((w) => (w.includes(id) ? w : [...w, id]))
  })
  usePushPoll(lateKey, LATE_POLL_MS, merge)

  if (page.error instanceof ApiError && page.error.status === 403) {
    return (
      <div className="alert alert-danger">
        <i className="fas fa-exclamation-circle" aria-hidden="true"></i> You do not have access to this action.
      </div>
    )
  }
  if (!page.data) return <Spinner />
  const data = page.data
  const show = (next: GzPage) => client.setQueryData(GZ_KEY, next)
  const own = data.connections.find((c) => c.scope === 'own')

  const runTest = async (c: GzConnection) => {
    if (busy) return
    setBusy(true)
    setTests((t) => ({ ...t, [c.id]: 'Testing…' }))
    try {
      const answer = await gzTestConnection(c.id)
      setTests((t) => ({ ...t, [c.id]: answer.ok ? `OK: ${answer.companies_seen ?? 0} companies visible.` : (answer.error ?? 'GravityZone did not answer.') }))
      void client.invalidateQueries({ queryKey: GZ_KEY })
    } catch (err) {
      setTests((t) => ({ ...t, [c.id]: errorOf(err).message }))
    } finally {
      setBusy(false)
    }
  }

  const remove = async (c: GzConnection) => {
    if (busy || !confirm(`Delete the connection "${c.name}"? Its API key is removed from OptimumDesk.`)) return
    setBusy(true)
    try {
      show(await gzDeleteConnection(c.id))
      toast('Connection deleted', 'Success', 'success')
    } catch (err) {
      const problem = errorOf(err)
      const canForce = err instanceof ApiError && err.details?.can_force === true
      if (canForce && confirm(`${problem.message}\n\nDelete the connection anyway? GravityZone keeps trying to send events until Push is turned off there.`)) {
        try {
          show(await gzDeleteConnection(c.id, true))
          toast('Connection deleted', 'Success', 'success')
        } catch (again) {
          toast(errorOf(again).message, 'Error', 'error')
        }
      } else if (!canForce) {
        toast(problem.message, 'Error', 'error')
      }
    } finally {
      setBusy(false)
    }
  }

  const enablePush = async (c: GzConnection) => {
    const rateLimited = (err: unknown) => err instanceof ApiError && err.status === 429 && typeof err.details?.retry_in === 'number'
    if (busy) return
    setBusy(true)
    const watch = () => setWatched((w) => (w.includes(c.id) ? w : [...w, c.id]))
    try {
      merge(c.id, (await gzEnablePush(c.id)).push)
      watch()
    } catch (err) {
      const problem = errorOf(err)
      if (err instanceof ApiError && err.status === 409 && err.details?.error === 'push_elsewhere') {
        if (confirm(`${problem.message}\n\nOptimumDesk becomes the only receiver of this account's events.`)) {
          try {
            merge(c.id, (await gzEnablePush(c.id, true)).push)
            watch()
          } catch (again) {
            toast(errorOf(again).message, 'Error', 'error')
          }
        }
      } else if (rateLimited(err) && c.push) {
        // GZ: 5 apeluri pe minut; butonul numara secundele ramase
        merge(c.id, { ...c.push, retry_in: Number((err as ApiError).details?.retry_in) })
      } else {
        toast(problem.message, 'Error', 'error')
      }
    } finally {
      setBusy(false)
    }
  }

  const turnOffPush = async (c: GzConnection) => {
    if (busy || !confirm(`Turn off Push for "${c.name}"? GravityZone stops sending events to OptimumDesk.`)) return
    setBusy(true)
    try {
      merge(c.id, (await gzPushOff(c.id)).push)
      toast('Push turned off', 'Success', 'success')
    } catch (err) {
      toast(errorOf(err).message, 'Error', 'error')
    } finally {
      setBusy(false)
    }
  }

  const pushShown = data.connections.filter((c) => c.push && (c.push.status === 'enabling' || c.push.status === 'failed' || watched.includes(c.id)))

  return (
    <div id="content" className="gz-panel">
      <header className="panel-heading">
        <h3>
          <strong>GravityZone</strong> (Bitdefender)
        </h3>
        <label className="color">link with the GravityZone Cloud console</label>
      </header>
      <p className="gz-intro">
        Securitee reads from GravityZone, when a company is linked, on Sync now and, with Push on, whenever GravityZone reports a change, the protection state of each endpoint: agent, protection modules, policy, malware status and risk score, and matches
        it to the OptimumDesk device (by MAC address, then by name). Only companies linked below are read. With <strong>Push</strong> on, GravityZone sends every change to OptimumDesk as it happens; turning
        Push on or off is the only thing changed in GravityZone.
      </p>
      {!data.cryptoReady && (
        <div className="alert alert-warning gz-crypto">API keys cannot be stored: encryption is not configured on the server (DEFUSE_CRYPT_PASS).</div>
      )}

      <section className="gz-section">
        <h4>Connections</h4>
        {data.connections.length === 0 ? (
          <p className="gz-empty">
            No GravityZone connection yet. In GravityZone, open <strong>My Account › API keys</strong>, add a key with the <strong>Network</strong>,{' '}
            <strong>Policies</strong> and <strong>Event Push Service</strong> APIs, and copy the <strong>Access URL</strong> shown under Control Center API.
          </p>
        ) : (
          <div className="gz-table-scroll">
            <table className="table gz-connections">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Used by</th>
                  <th>Access URL</th>
                  <th>Status</th>
                  <th>Push</th>
                  <th aria-label="Actions"></th>
                </tr>
              </thead>
              <tbody>
                {data.connections.map((c) => (
                  <tr key={c.id}>
                    <td className="gz-name">{c.name}</td>
                    <td>{c.scope === 'shared' ? 'Shared' : 'This company'}</td>
                    <td className="gz-url">{c.access_url}</td>
                    <td>
                      {connectionStatus(c)}
                      {tests[c.id] && (
                        <div className="gz-test-result" role="status">
                          {tests[c.id]}
                        </div>
                      )}
                    </td>
                    <td>{c.push ? pushLabel(c.push) : <span className="gz-muted">—</span>}</td>
                    <td className="gz-actions">
                      {c.can_edit && (
                        <>
                          {c.push && (c.push.status === 'off' || c.push.status === 'failed') && (
                            <PushButton key={`${c.id}:${c.push.retry_in ?? 0}`} name={c.name} retry={c.push.status === 'failed'} wait={c.push.retry_in ?? 0} busy={busy} onClick={() => void enablePush(c)} />
                          )}
                          {c.push && (c.push.status === 'active' || c.push.status === 'failed') && (
                            <button type="button" className="btn btn-default btn-xs" aria-label={`Turn off Push for ${c.name}`} disabled={busy} onClick={() => void turnOffPush(c)}>
                              Turn off Push
                            </button>
                          )}
                          <button type="button" className="btn btn-default btn-xs" aria-label={`Test ${c.name}`} disabled={busy} onClick={() => void runTest(c)}>
                            Test
                          </button>
                          <button
                            type="button"
                            className="btn btn-default btn-xs"
                            aria-label={`Edit ${c.name}`}
                            disabled={busy}
                            onClick={() => setEditing({ id: c.id, scope: c.scope, name: c.name, access_url: c.access_url, api_key: '' })}
                          >
                            Edit
                          </button>
                          <button type="button" className="btn btn-default btn-xs" aria-label={`Delete ${c.name}`} disabled={busy} onClick={() => void remove(c)}>
                            Delete
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {pushShown.map((c) => (
          <PushSteps key={c.id} connection={c} push={c.push!} />
        ))}
        {editing === null && (data.canManageShared || (data.canWrite && !own)) && (
          <div className="gz-add">
            {data.canManageShared && (
              <button type="button" className="btn btn-default" onClick={() => setEditing({ scope: 'shared', name: '', access_url: CLOUD_URL, api_key: '' })}>
                Add shared connection
              </button>
            )}
            {data.canWrite && !own && (
              <button type="button" className="btn btn-default" onClick={() => setEditing({ scope: 'own', name: '', access_url: CLOUD_URL, api_key: '' })}>
                Add this company&apos;s console
              </button>
            )}
          </div>
        )}
        {editing !== null && (
          <ConnectionForm
            key={editing.id ?? editing.scope}
            initial={editing}
            keySaved={editing.id !== undefined && (data.connections.find((c) => c.id === editing.id)?.key_set ?? false)}
            onSaved={(next) => {
              show(next)
              setEditing(null)
              toast('Connection saved', 'Success', 'success')
            }}
            onCancel={() => setEditing(null)}
          />
        )}
      </section>

      <section className="gz-section">
        <h4>This company in GravityZone</h4>
        {data.link !== null ? (
          <LinkedCompany
            link={data.link}
            canWrite={data.canWrite}
            onUnlinked={show}
            onSynced={(next) => {
              show(next)
              setPollUntil(Date.now() + 120000)
            }}
          />
        ) : !data.canWrite ? (
          <p className="gz-empty">Not linked to a GravityZone company.</p>
        ) : data.connections.length === 0 ? (
          <p className="gz-empty">Add a connection first.</p>
        ) : (
          <LinkForm
            connections={data.connections}
            onLinked={(next) => {
              show(next)
              setPollUntil(Date.now() + 120000)
            }}
          />
        )}
      </section>
    </div>
  )
}

function ConnectionForm({
  initial,
  keySaved,
  onSaved,
  onCancel,
}: {
  initial: GzConnectionForm
  keySaved: boolean
  onSaved: (page: GzPage) => void
  onCancel: () => void
}) {
  const [draft, setDraft] = useState(initial)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [general, setGeneral] = useState('')
  const [busy, setBusy] = useState(false)
  const form = useRef<HTMLFormElement>(null)

  /* Focus pe primul camp rosu, ca omul sa vada unde e problema. */
  const show = (next: Record<string, string>) => {
    setErrors(next)
    const first = FORM_FIELDS.find((name) => next[name])
    if (first) requestAnimationFrame(() => form.current?.querySelector<HTMLInputElement>('#gz-' + first)?.focus())
  }

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    if (busy) return
    setGeneral('')
    const missing: Record<string, string> = {}
    if (draft.name.trim() === '') missing.name = 'Required.'
    if (draft.access_url.trim() === '') missing.access_url = 'Required.'
    if (!keySaved && draft.api_key.trim() === '') missing.api_key = 'Required.'
    if (Object.keys(missing).length > 0) {
      show(missing)
      return
    }
    setBusy(true)
    setErrors({})
    try {
      onSaved(await gzSaveConnection(draft))
    } catch (err) {
      const problem = errorOf(err)
      if (FORM_FIELDS.includes(problem.field)) show({ [problem.field]: problem.message })
      else setGeneral(problem.message)
    } finally {
      setBusy(false)
    }
  }

  const field = (name: keyof GzConnectionForm & string, label: string, input: Record<string, unknown>, help?: string) => {
    const message = errors[name]
    const id = 'gz-' + name
    return (
      <div className={'form-group' + (message ? ' has-error' : '')}>
        <label htmlFor={id}>{label}</label>
        <input
          id={id}
          className="form-control"
          autoComplete="off"
          spellCheck={false}
          disabled={busy}
          value={String(draft[name] ?? '')}
          onChange={(e) => setDraft((d) => ({ ...d, [name]: e.target.value }))}
          aria-invalid={message ? 'true' : undefined}
          aria-describedby={message ? id + '-error' : undefined}
          {...input}
        />
        {message ? (
          <span className="help-block gz-field-error" id={id + '-error'}>
            {message}
          </span>
        ) : (
          help && <span className="help-block">{help}</span>
        )}
      </div>
    )
  }

  const hasErrors = Object.keys(errors).length > 0

  return (
    <form ref={form} className="gz-form" noValidate onSubmit={(e) => void submit(e)}>
      <h5>
        <strong>{initial.id ? 'Edit connection' : initial.scope === 'shared' ? 'New shared connection' : "This company's own GravityZone console"}</strong>
      </h5>
      <p className="help-block">
        {initial.scope === 'shared'
          ? 'Shared: the API key of your partner account. Every company of yours can be linked through it.'
          : 'Only for this company: use it when the company has its own GravityZone console.'}
      </p>
      {field('name', 'Name', { type: 'text', maxLength: 100 })}
      {field('access_url', 'Access URL', { type: 'url' }, 'GravityZone › My Account › Control Center API.')}
      {field(
        'api_key',
        'API key',
        { type: 'password', autoComplete: 'new-password', placeholder: keySaved ? 'Saved. Leave empty to keep it.' : '' },
        'Network and Policies APIs to read; Event Push Service for Push. Stored encrypted; it is never shown again.',
      )}
      {general !== '' && <div className="alert alert-danger">{general}</div>}
      <div className="gz-form-buttons">
        <button type="submit" className="btn btn-primary" disabled={busy}>
          Save
        </button>
        <button type="button" className="btn btn-default" disabled={busy} onClick={onCancel}>
          Cancel
        </button>
        {hasErrors && (
          <span className="gz-form-alert" role="alert">
            <i className="fas fa-exclamation-circle" aria-hidden="true"></i> Check the fields marked in red.
          </span>
        )}
      </div>
    </form>
  )
}

function LinkForm({ connections, onLinked }: { connections: GzConnection[]; onLinked: (page: GzPage) => void }) {
  const [connectionId, setConnectionId] = useState(connections[0]!.id)
  const [companies, setCompanies] = useState<GzCompany[] | null>(null)
  const [chosen, setChosen] = useState('')
  const [networks, setNetworks] = useState<string[] | null>(null)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)

  const load = async () => {
    if (busy) return
    setBusy(true)
    setError('')
    try {
      setCompanies((await gzCompanies(connectionId)).companies)
      setChosen('')
    } catch (err) {
      setError(errorOf(err).message)
    } finally {
      setBusy(false)
    }
  }

  const link = async () => {
    if (busy || chosen === '') return
    setBusy(true)
    setError('')
    try {
      const next = await gzLink(connectionId, chosen, networks ?? undefined)
      onLinked(next)
      if (next.sync?.started) toast('Company linked. The first sync has started.', 'Success', 'success')
      else toast(`Company linked, but the first sync did not start: ${next.sync?.error ?? 'unknown reason'}. Use Sync now.`, 'Warning', 'warning')
    } catch (err) {
      setError(errorOf(err).message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="gz-link-form">
      <div className="gz-link-row">
        {connections.length > 1 ? (
          <div className="form-group">
            <label htmlFor="gz-link-connection">Connection</label>
            <select
              id="gz-link-connection"
              className="form-control"
              value={connectionId}
              disabled={busy}
              onChange={(e) => {
                setConnectionId(Number(e.target.value))
                setCompanies(null)
              }}
            >
              {connections.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
        ) : (
          <p className="gz-link-via">
            Through <strong>{connections[0]!.name}</strong>
          </p>
        )}
        <button type="button" className="btn btn-default" disabled={busy} onClick={() => void load()}>
          Load companies
        </button>
      </div>
      {companies !== null && (
        <div className="gz-link-row">
          <div className="form-group">
            <label htmlFor="gz-link-company">GravityZone company</label>
            <select
              id="gz-link-company"
              className="form-control"
              value={chosen}
              disabled={busy}
              onChange={(e) => {
                setChosen(e.target.value)
                setNetworks(null)
              }}
            >
              <option value="">Choose a company…</option>
              {companies.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
            <span className="help-block">Read live from GravityZone. Only linked companies are synced; the rest of the account is ignored.</span>
          </div>
          <button type="button" className="btn btn-primary" disabled={busy || chosen === '' || networks?.length === 0} onClick={() => void link()}>
            Link
          </button>
        </div>
      )}
      {companies !== null && chosen !== '' && (
        <NetworksPicker key={connectionId + ' ' + chosen} connectionId={connectionId} gzCompanyId={chosen} saved={null} disabled={busy} onChange={setNetworks} />
      )}
      {error !== '' && <div className="alert alert-danger">{error}</div>}
    </div>
  )
}

function LinkedCompany({
  link,
  canWrite,
  onUnlinked,
  onSynced,
}: {
  link: GzLink
  canWrite: boolean
  onUnlinked: (page: GzPage) => void
  onSynced: (page: GzPage) => void
}) {
  const [busy, setBusy] = useState(false)
  const [syncMessage, setSyncMessage] = useState<{ ok: boolean; text: string } | null>(null)
  const [choosing, setChoosing] = useState(false)
  const [networks, setNetworks] = useState<string[] | null>(null)

  const saveNetworks = async () => {
    if (busy || networks === null || networks.length === 0) return
    setBusy(true)
    try {
      onSynced(await gzLink(link.connection_id, link.gz_company_id, networks))
      setChoosing(false)
      toast('Networks saved. They are read from the next sync.', 'Success', 'success')
    } catch (err) {
      toast(errorOf(err).message, 'Error', 'error')
    } finally {
      setBusy(false)
    }
  }

  const syncNow = async () => {
    if (busy) return
    setBusy(true)
    setSyncMessage(null)
    try {
      onSynced(await gzSyncNow())
      setSyncMessage({ ok: true, text: 'Sync started. The endpoints appear here in a minute or two.' })
    } catch (err) {
      setSyncMessage({ ok: false, text: errorOf(err).message })
    } finally {
      setBusy(false)
    }
  }

  const unlink = async () => {
    if (busy || !confirm('Unlink this company from GravityZone? The endpoint data read from GravityZone is removed.')) return
    setBusy(true)
    try {
      onUnlinked(await gzUnlink())
      toast('Unlinked', 'Success', 'success')
    } catch (err) {
      toast(errorOf(err).message, 'Error', 'error')
    } finally {
      setBusy(false)
    }
  }

  return (
    <table className="table gz-link">
      <tbody>
        <tr>
          <th>GravityZone company</th>
          <td>
            <strong>{link.gz_company_name}</strong>
            {link.connection_name && <span className="gz-muted"> · through {link.connection_name}</span>}
          </td>
        </tr>
        <tr>
          <th>Last sync</th>
          <td>
            {link.synced_at ?? 'Waiting for the first sync. Press Sync now to start it.'}
            {link.sync_error && <span className="label label-danger gz-sync-error">{link.sync_error}</span>}
          </td>
        </tr>
        <tr>
          <th>Networks</th>
          <td>
            {link.networks ? `${link.networks.length} ${link.networks.length === 1 ? 'network' : 'networks'} ticked` : 'All networks of the company'}
            {canWrite && !choosing && (
              <button type="button" className="btn btn-link btn-xs" disabled={busy} onClick={() => setChoosing(true)}>
                Choose networks
              </button>
            )}
            {choosing && (
              <>
                <NetworksPicker connectionId={link.connection_id} gzCompanyId={link.gz_company_id} saved={link.networks ?? null} disabled={busy} onChange={setNetworks} />
                <span className="gz-link-actions">
                  <button type="button" className="btn btn-primary btn-sm" disabled={busy || networks === null || networks.length === 0} onClick={() => void saveNetworks()}>
                    Save networks
                  </button>
                  <button type="button" className="btn btn-default btn-sm" disabled={busy} onClick={() => setChoosing(false)}>
                    Cancel
                  </button>
                </span>
              </>
            )}
          </td>
        </tr>
        {link.endpoints !== null && (
          <tr>
            <th>Matched devices</th>
            <td>
              {link.matched ?? 0} of {link.endpoints} GravityZone endpoints matched to OptimumDesk devices
            </td>
          </tr>
        )}
        {canWrite && (
          <tr>
            <th></th>
            <td>
              <span className="gz-link-actions">
                <button type="button" className="btn btn-primary" disabled={busy} onClick={() => void syncNow()}>
                  Sync now
                </button>
                <button type="button" className="btn btn-default" disabled={busy} onClick={() => void unlink()}>
                  Unlink
                </button>
              </span>
              {syncMessage && (
                <div className={'gz-sync-message ' + (syncMessage.ok ? 'text-success' : 'text-danger')} role="status">
                  {syncMessage.text}
                </div>
              )}
            </td>
          </tr>
        )}
      </tbody>
    </table>
  )
}

/**
 * Retelele firmei din GZ, cu bife: "(calculatoare in GZ / gasite si in OD)",
 * ca omul sa vada ce merita citit - o retea "0 / 0" se debifeaza. Fara nimic
 * salvat, toate vin bifate. `onChange(null)` = retelele nu s-au putut citi,
 * deci Link le citeste pe toate.
 */
function NetworksPicker({
  connectionId,
  gzCompanyId,
  saved,
  disabled,
  onChange,
}: {
  connectionId: number
  gzCompanyId: string
  saved: string[] | null
  disabled: boolean
  onChange: (ticked: string[] | null) => void
}) {
  const [networks, setNetworks] = useState<GzNetwork[] | null>(null)
  const [ticked, setTicked] = useState<string[]>([])
  const [error, setError] = useState('')

  useEffect(() => {
    let live = true
    gzNetworks(connectionId, gzCompanyId)
      .then((answer) => {
        if (!live) return
        const ids = answer.networks.map((n) => n.id)
        const start = saved === null ? ids : ids.filter((id) => saved.includes(id))
        setNetworks(answer.networks)
        setTicked(start)
        onChange(start)
      })
      .catch((err: unknown) => {
        if (!live) return
        setError(errorOf(err).message)
        onChange(null)
      })
    return () => {
      live = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- o citire pe firma aleasa
  }, [connectionId, gzCompanyId])

  const toggle = (id: string) => {
    const next = ticked.includes(id) ? ticked.filter((t) => t !== id) : networks!.map((n) => n.id).filter((n) => n === id || ticked.includes(n))
    setTicked(next)
    onChange(next)
  }

  if (error !== '') {
    return <p className="help-block text-warning">The networks could not be read ({error}). Linking reads all of them.</p>
  }
  if (networks === null) return <Spinner />

  return (
    <fieldset className="gz-networks">
      <legend>Networks to sync</legend>
      {networks.map((n) => (
        <div className="checkbox" key={n.id}>
          <label>
            <input type="checkbox" checked={ticked.includes(n.id)} disabled={disabled} onChange={() => toggle(n.id)} />
            {`${n.name} (${n.computers} / ${n.in_od})`}
          </label>
        </div>
      ))}
      <span className="help-block">(computers in GravityZone / also in OptimumDesk). A network with 0 / 0 has nothing to read.</span>
    </fieldset>
  )
}
